import * as React from 'react';
import { composePaths } from '@jsonforms/core';
import {
  StackLayout,
  StackItem,
  Box,
  Button,
} from '@leagueplatform/genesis-core';
import { JsonFormsDispatch } from '@jsonforms/react';
import { useRuleTemplatesEditorStateStoreActions } from '@web-config-app/core-react';
import {
  EntityFormArrayControl,
  ArrayPrimitiveGroup,
  ArrayActionButtons,
  EmptyArrayState,
  useEntityDetailsProps,
  useArrayPrimitiveActions,
} from '@web-config-app/entity-form';
import type {
  ArrayControl,
  EntityFormArrayControlProps,
} from '@web-config-app/entity-form';
import { useRuleCombinationContext } from '../../controls-primitive/json-logic-input-control/rule-combination-provider/rule-combination-provider.component';

export const ArrayJsonLogicContents = ({
  hint,
  label,
  banner,
  data,
  errors,
  addItem,
  hideLabel,
  removeItems,
  moveDown,
  moveUp,
  arrayAddLabel,
  path,
  schema,
  rootSchema,
  uischemas = [],
  uischema,
  renderers,
  enabled,
}: EntityFormArrayControlProps) => {
  const entityDetailsProps = useEntityDetailsProps();
  const formPath = entityDetailsProps?.formPath;

  const { removeFromNestedFieldsArray, addNestedFieldToArray } =
    useRuleTemplatesEditorStateStoreActions();

  const ruleContext = useRuleCombinationContext();

  const { childUiSchema, handleCreateDefaultValue, handleRemoveItem } =
    useArrayPrimitiveActions({
      removeItems,
      moveDown,
      moveUp,
      rootSchema,
      uischemas,
      uischema,
      path,
      schema,
      data,
      formPath,
    });

  const arrayItemsEmpty = !data || data?.length === 0;

  const arrayLength = data?.length;

  const deleteItem = React.useCallback(
    (itemPath: string, index: number) => () => {
      handleRemoveItem(itemPath, index)();
      if (ruleContext?.combinationFieldPath) {
        removeFromNestedFieldsArray?.({
          fieldPath: ruleContext.combinationFieldPath,
          arrayIndex: index,
        });
      }
    },
    [
      ruleContext?.combinationFieldPath,
      handleRemoveItem,
      removeFromNestedFieldsArray,
    ],
  );

  const handleAddItem = React.useCallback(() => {
    if (ruleContext?.combinationFieldPath) {
      addNestedFieldToArray?.({
        fieldPath: ruleContext.combinationFieldPath,
      });
    }
    addItem(path, handleCreateDefaultValue())();
  }, [
    addNestedFieldToArray,
    ruleContext?.combinationFieldPath,
    addItem,
    path,
    handleCreateDefaultValue,
  ]);

  return (
    // re-renders when array length changes to handle bug when the form was not re-rendering when a rule was being deleted!!
    <Box
      css={{
        marginBlockStart: `${hideLabel ? '$none' : '1.25rem'}`,
        marginBlockEnd: '$two',
      }}
      key={arrayLength}
    >
      <ArrayPrimitiveGroup
        hint={hint}
        label={label}
        banner={banner}
        errors={errors}
        hideLabel={hideLabel}
      >
        {arrayItemsEmpty ? (
          <EmptyArrayState item={label} />
        ) : (
          <StackLayout
            role="list"
            aria-label={label}
            css={{
              listStyleType: 'none',
              paddingInlineStart: '$none',
              width: '100%',
            }}
            spacing="$half"
          >
            {data?.map((_: any, index: number) => {
              const childPath = composePaths(path, `${index}`);

              return (
                <StackLayout
                  role="listitem"
                  key={childPath}
                  orientation="horizontal"
                  verticalAlignment="center"
                  spacing="$half"
                  css={{
                    width: '100%',
                    backgroundColor: '$surfaceBackgroundPrimary',
                    borderRadius: '$medium',
                    padding: '$half',
                  }}
                >
                  <StackItem css={{ width: '100%' }}>
                    <JsonFormsDispatch
                      schema={schema}
                      uischema={childUiSchema || uischema}
                      path={childPath}
                      key={childPath}
                      renderers={renderers}
                    />
                  </StackItem>
                  <StackItem>
                    <ArrayActionButtons
                      arrayLength={data.length}
                      itemIndex={index}
                      itemLabel={label}
                      onDelete={deleteItem(path, index)}
                      disableAllActions={!enabled}
                    />
                  </StackItem>
                </StackLayout>
              );
            })}
          </StackLayout>
        )}
      </ArrayPrimitiveGroup>
      <Button
        icon="interfacePlus"
        size="medium"
        priority="secondary"
        onClick={() => handleAddItem()}
        css={{ marginBlockStart: '$one' }}
        disabled={!enabled}
      >
        {arrayAddLabel}
      </Button>
    </Box>
  );
};

const renderControl = (formContentsProps: EntityFormArrayControlProps) => (
  <ArrayJsonLogicContents {...formContentsProps} />
);

export const ArrayJsonLogicControl: ArrayControl = (props) => (
  <EntityFormArrayControl {...props} renderControl={renderControl} />
);
